import { useState } from 'react';
import { PiggyBank, HelpCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import ToolPage from '@/components/ToolPage';
import { toolCardClass, toolInputClass, toolLabelClass, toolButtonOutlineClass } from '@/lib/toolStyles';

interface Expense {
  name: string;
  amount: number;
}

const defaultExpenses: Expense[] = [
  { name: 'Rent/Mortgage', amount: 0 },
  { name: 'Utilities', amount: 0 },
  { name: 'Food & Groceries', amount: 0 },
  { name: 'Transportation', amount: 0 },
  { name: 'Insurance', amount: 0 },
  { name: 'Healthcare', amount: 0 },
  { name: 'Debt Payments', amount: 0 },
  { name: 'Other Essential Expenses', amount: 0 },
];

const coveragePeriods = [
  { months: 3, label: '3 months (minimum recommended)' },
  { months: 6, label: '6 months (standard recommended)' },
  { months: 9, label: '9 months (conservative)' },
  { months: 12, label: '12 months (very conservative)' },
];

export default function EmergencyFund() {
  const [expenses, setExpenses] = useState<Expense[]>(defaultExpenses);
  const [selectedPeriod, setSelectedPeriod] = useState(6);
  const [showInfo, setShowInfo] = useState(false);

  const totalMonthlyExpenses = expenses.reduce((sum, exp) => sum + exp.amount, 0);
  const recommendedFund = totalMonthlyExpenses * selectedPeriod;

  const handleExpenseChange = (index: number, amount: string) => {
    const newExpenses = [...expenses];
    newExpenses[index].amount = Number(amount) || 0;
    setExpenses(newExpenses);
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);
  };

  return (
    <ToolPage
      title="Emergency Fund Calculator"
      description="Calculate how much you should save for emergencies"
      icon={PiggyBank}
    >
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        <Card className={toolCardClass}>
          <CardHeader>
            <h2 className="text-lg font-semibold text-slate-900">Monthly Essential Expenses</h2>
          </CardHeader>
          <CardContent className="space-y-4">
            {expenses.map((expense, index) => (
              <div key={expense.name} className="flex items-center gap-4">
                <Label className={`w-1/2 ${toolLabelClass}`}>{expense.name}</Label>
                <div className="w-1/2 relative">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500">$</span>
                  <Input
                    type="number"
                    min={0}
                    value={expense.amount || ''}
                    onChange={(e) => handleExpenseChange(index, e.target.value)}
                    className={`pl-8 ${toolInputClass}`}
                  />
                </div>
              </div>
            ))}
            <div className="pt-4">
              <Label className={`mb-2 block ${toolLabelClass}`}>Coverage Period</Label>
              <div className="grid grid-cols-2 gap-4">
                {coveragePeriods.map((period) => (
                  <Button
                    key={period.months}
                    variant={selectedPeriod === period.months ? 'gradient' : 'outline'}
                    className={`h-auto py-3 text-left ${
                      selectedPeriod !== period.months ? toolButtonOutlineClass : ''
                    }`}
                    onClick={() => setSelectedPeriod(period.months)}
                  >
                    {period.label}
                  </Button>
                ))}
              </div>
            </div>
          </CardContent>
        </Card>

        <div>
          <Card className={`mb-6 ${toolCardClass}`}>
            <CardHeader className="flex flex-row justify-between items-center">
              <h2 className="text-lg font-semibold text-slate-900">Results</h2>
              <Button variant="ghost" size="icon" onClick={() => setShowInfo(!showInfo)} className="text-slate-500 hover:text-slate-900 hover:bg-slate-100">
                <HelpCircle className="w-5 h-5" />
              </Button>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <p className={`text-sm ${toolLabelClass} mb-1`}>Total Monthly Expenses</p>
                <p className="text-2xl font-bold text-slate-900">{formatCurrency(totalMonthlyExpenses)}</p>
              </div>
              <div className="pt-4 border-t border-slate-200">
                <p className={`text-sm ${toolLabelClass} mb-1`}>Recommended Emergency Fund ({selectedPeriod} months)</p>
                <p className="text-3xl font-bold text-brand-700">
                  {formatCurrency(recommendedFund)}
                </p>
              </div>
            </CardContent>
          </Card>

          {showInfo && (
            <Card className="border-brand-200 bg-brand-50">
              <CardContent className="pt-6">
                <h3 className="font-semibold text-slate-900 mb-2">About Emergency Funds</h3>
                <div className={`text-sm ${toolLabelClass} space-y-2`}>
                  <p>An emergency fund covers unexpected expenses like job loss, medical emergencies, or urgent repairs.</p>
                  <p>Most experts recommend 3-6 months of essential expenses. The right amount depends on job stability, household income, and obligations.</p>
                </div>
              </CardContent>
            </Card>
          )}
        </div>
      </div>
    </ToolPage>
  );
}
